/**
 * AuthContext.js
 * Manages authentication state across the React application
 * 
 * Usage:
 *   import { useAuth } from './AuthContext';
 *   const { user, token, login, logout, isAuthenticated } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from './config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-company chat capture kill switch (Rule 103). Sourced from /me — never
  // from the login response or JWT — so an admin toggle takes effect on the
  // next app reload without requiring re-login (D.1b). Default false keeps
  // capture UI fully dormant until the backend returns a true value.
  const [chatCaptureEnabled, setChatCaptureEnabled] = useState(false);
  // Per-company scorecard UI kill switch (§5.BT §3.3 P3 Commit A, 2026-06-19).
  // Same /me-sourced pattern + same default-false posture as chatCaptureEnabled.
  // Frontend gate: when FALSE, the Performance tab is excluded from the dossier
  // tab strip (Layer composes with Commit A's backend-empty-payload gate +
  // Layer 1 zero-PROD-data baseline — see P2 §A three-layer defense).
  const [scorecardUiEnabled, setScorecardUiEnabled] = useState(false);
  // Per-company inventory UI kill switch (§5.BU §I.3 P3 Commit C, 2026-06-19).
  // Same shape as scorecardUiEnabled — Layer 3 of the inventory v1 three-layer
  // PROD-invisibility defense. When FALSE the Inventory tab is excluded from
  // the dossier tab strip.
  const [inventoryUiEnabled, setInventoryUiEnabled] = useState(false);
  // Opportunities module flag (Lane 42). Unlike the three flags above this is
  // ENV-level (OPPORTUNITIES_ENABLED — same value for every company), still
  // delivered via /me. When FALSE the Opportunities nav item is not rendered
  // and the /opportunities routes are not registered (App.js), so the module
  // leaves no trace. Default false = dormant until the backend says otherwise.
  const [opportunitiesEnabled, setOpportunitiesEnabled] = useState(false);
  // Lead-time surfaces flag (Lane 156, Track B / B4). ENV-level
  // (OPPORTUNITIES_LEADTIME_ENABLED — the ONE flag every lead-time surface
  // rides: the Opportunities family, the Family-1 chip, the deck slide, and
  // the dossier panels), delivered via /me exactly like opportunitiesEnabled.
  // When FALSE the dossier lead-time components never fetch and render null.
  const [leadtimeUiEnabled, setLeadtimeUiEnabled] = useState(false);
  // Template decks flag (Lane 1410 W4a, 2026-10-16). ENV-level
  // (WINGMAN_TEMPLATE_DECKS_ENABLED, the flag of the Meeting Prep library and
  // the chat deck builds), delivered via /me exactly like opportunitiesEnabled.
  // The Sidebar "Meeting Prep" item and the Home "Prepare for a meeting" button
  // read this and hide themselves when FALSE, so no tenant is shown a door to a
  // dark Meeting Prep page. Default false = dormant until the backend says
  // otherwise. It replaced deckIntentEnabled (DECK_MEETING_INTENT_ENABLED, a
  // flag of the retired deck creation engines).
  const [templateDecksEnabled, setTemplateDecksEnabled] = useState(false);
  // Parts-list supplier-page flow gate.  Sourced from /me and defaulted off so
  // chat never offers a destination whose create endpoint is dark.
  // Delivery-reliability surfaces flag (Lane 295, plan Lanes 2 + 5). ENV-level
  // (DELIVERY_RELIABILITY_ENABLED — the ONE flag every delivery-reliability
  // surface rides: slippage + the actual-lead-time reframe), delivered via /me
  // exactly like leadtimeUiEnabled. When FALSE the dossier Delivery section
  // never renders and its panel never fetches (double OFF-lock). Default false
  // = dark until the backend says otherwise.
  const [deliveryReliabilityEnabled, setDeliveryReliabilityEnabled] = useState(false);
  // Inventory & Ordering worklist flag (Lane 500). ENV-level
  // (INVENTORY_ORDERING_ENABLED), delivered via /me exactly like
  // opportunitiesEnabled. When FALSE the nav item and the /inventory route are
  // never registered and the page never fetches (double OFF-lock, with the
  // endpoint 404ing independently). Default false = dark until the backend
  // says otherwise.
  const [inventoryOrderingEnabled, setInventoryOrderingEnabled] = useState(false);
  // Chat workbooks (Lane 1132). workbookExportEnabled is the EFFECTIVE
  // creation gate echoed by /me (global env flag AND the per-company
  // opt-in, both default OFF); the backend endpoints enforce the same
  // gate independently. hasReportArtifacts keeps the Reports nav/route
  // reachable READ-ONLY after a rollback (flag OFF but files exist), so
  // customer files are never stranded. Both default false = dark.
  const [workbookExportEnabled, setWorkbookExportEnabled] = useState(false);
  const [hasReportArtifacts, setHasReportArtifacts] = useState(false);
  // Sourcing events (Lane 1322): the EFFECTIVE gate echoed by /me (global
  // SOURCING_EVENTS_ENABLED AND the company's switch). Default false = dark.
  const [sourcingEventsEnabled, setSourcingEventsEnabled] = useState(false);
  // RFPs from chat (Lane 1368): SOURCING_CHAT_INTAKE_ENABLED, effective only with the
  // sourcing gate. /me omits the key while off, so the default false is the off state.
  const [sourcingChatIntakeEnabled, setSourcingChatIntakeEnabled] = useState(false);
  // Lane 1439: the redesigned RFP card in Home chat. /me omits the key while off.
  const [sourcingIntakeCardV2Enabled, setSourcingIntakeCardV2Enabled] = useState(false);
  // Uploaded spreadsheets as tables (Lane 1400 WS3): CHAT_UPLOAD_TABLES_ENABLED.
  // /me omits the key while off, so the default false is the off state.
  const [uploadTablesEnabled, setUploadTablesEnabled] = useState(false);
  // Lane 1405 v3: the /me echo of the deck builder (the Build a deck button).
  const [deckBuilderEnabled, setDeckBuilderEnabled] = useState(false);
  // Lane 1369: true once this session's /me call has finished (answered or
  // failed), so the flags above are final. After a fresh sign-in /me runs in
  // the background; a page that must choose a destination from a flag (the
  // retired old RFP builder URLs, App.js) waits for this instead of acting on
  // the false defaults. Reset by logout.
  const [meFlagsResolved, setMeFlagsResolved] = useState(false);
  // Quoting tab (Lane 1335, RFQ plan C1): the company's rfq_mode is 'live'.
  // Tenant-level only (D5). Default false = dark.
  const [quotingEnabled, setQuotingEnabled] = useState(false);
  const [intelligencePortalEnabled, setIntelligencePortalEnabled] = useState(false);
  // Chat-first Home landing (Lane 1211). /me echo of
  // HOME_CHAT_FIRST_LANDING_ENABLED (env-level, default OFF). ON => the Home
  // empty-state renders prompt guidance + one example + the full-guide link
  // (no quick-action buttons) with a calm composer focus, and /prompt-guide is
  // routed. OFF => the current landing, byte-identical.
  const [homeChatFirstLandingEnabled, setHomeChatFirstLandingEnabled] = useState(false);
  // Supplier dossier notices + decisions (Lane 1342). /me echo of
  // CHAT_MIRROR_SUPPLIER_DECISIONS_ENABLED (env-level, default OFF), the same
  // flag that gates the chat half. OFF => the dossier is unchanged.
  const [supplierDecisionsEnabled, setSupplierDecisionsEnabled] = useState(false);
  // Doc-upload flag (Lane 616, Phase 5). PER-COMPANY DB
  // (companies.doc_upload_enabled — the Rule 103 kill switch the upload
  // pipeline itself checks), delivered via /me exactly like
  // chatCaptureEnabled. When FALSE the chat composer's attach control never
  // renders and no upload request can be made (double OFF-lock, with the
  // endpoint's own kill-switch check returning status='disabled'
  // independently). Default false = dark until the backend says otherwise.
  const [docUploadEnabled, setDocUploadEnabled] = useState(false);
  // Negotiation-brief flag (Lane 763, Phase 1b). PER-COMPANY DB
  // (companies.negotiation_brief_enabled — the Rule 103 kill switch
  // POST /api/chat/brief/evaluate checks, and the extractor helper re-checks),
  // delivered via /me exactly like chatCaptureEnabled.
  // ⚠️ IT IS NOT chatCaptureEnabled, AND THAT IS THE POINT: chat capture is
  // already ON for XMEK in production, so a second extractor riding that flag
  // would go live to a real customer on deploy. This one stores the buyer's
  // private negotiating stance, so it defaults FALSE for every tenant. When
  // FALSE, Home never fires the brief request at all (double OFF-lock, with
  // the endpoint returning status='disabled' independently).
  const [negotiationBriefEnabled, setNegotiationBriefEnabled] = useState(false);
  // Chat-waits-for-the-document flag (Lane 690). ENV-level
  // (CHAT_AWAITS_DOCUMENT_ENABLED), delivered via /me exactly like
  // opportunitiesEnabled. When FALSE, handleChatSubmit takes its pre-Lane-690
  // branch exactly: uploads fire alongside the turn and the answer is composed
  // without waiting. Default false = today's behaviour until the backend says
  // otherwise.
  const [chatAwaitsDocumentEnabled, setChatAwaitsDocumentEnabled] = useState(false);
  // Honest uploads (Lane 1400 WS1). ENV-level (CHAT_UPLOAD_HONESTY_ENABLED),
  // delivered via /me. TRUE: the chat tells the server about every file of a
  // Send (readable or not, and files that never uploaded), sends the
  // conversation id with each upload, and shows "I couldn't read <file>"
  // above the answer. Strictly `=== true`, so an older backend that omits the
  // field keeps today's behaviour.
  const [chatUploadHonestyEnabled, setChatUploadHonestyEnabled] = useState(false);
  // Duplicate-upload-stops-blocking flag (Lane 714, D50.5). ENV-level
  // (DUPLICATE_NOTICE_NONBLOCKING_ENABLED), delivered via /me exactly like
  // chatAwaitsDocumentEnabled. When FALSE the duplicate card is byte-identical
  // to today: "Wingman already has this" plus a mandatory "Read it again
  // anyway" button. When TRUE Wingman fires that re-read itself and says so, so
  // the buyer's next step is the sender confirm rather than a click that only
  // repeats what they already asked for. Default false = today's behaviour
  // until the backend says otherwise.
  const [duplicateNonblockingEnabled, setDuplicateNonblockingEnabled] = useState(false);
  // Always-on-capture flag (Lane 863). ENV-level (ALWAYS_ON_CAPTURE_ENABLED),
  // delivered via /me exactly like chatAwaitsDocumentEnabled. It retires the
  // per-USER consent gate: when TRUE the one-time consent banner is not
  // rendered, chat capture and the negotiation-brief extractor stop consulting
  // the per-user localStorage answer, and a standing disclosure line sits in the
  // chat footer instead.
  // ⚠️ IT IS NOT chatCaptureEnabled, AND IT CANNOT SUBSTITUTE FOR IT. The
  // per-COMPANY switches (chatCaptureEnabled / negotiationBriefEnabled) are the
  // real gates and are untouched by this flag — turning this on cannot enable
  // capture for a company that has it off. This one only decides whether the
  // per-user browser answer is still consulted on top of them.
  // Default false = today's behaviour, banner included, until the backend says
  // otherwise.
  const [alwaysOnCaptureEnabled, setAlwaysOnCaptureEnabled] = useState(false);

  // Check for existing token on app load
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);

        // Verify token is still valid using appropriate endpoint
        verifyToken(storedToken, parsedUser.user_type).then(valid => {
          if (!valid) {
            logout();
          }
          setLoading(false);
        });
      } catch (e) {
        logout();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Verify token with backend. On success, also parse the response body to
  // surface top-level flags into context state: the per-company flags
  // (chat_capture_enabled, scorecard_ui_enabled, inventory_ui_enabled) and the
  // env-level opportunities_enabled. Supplier /me does not return these flags —
  // that path leaves the defaults (false) alone.
  const verifyToken = async (tokenToVerify, userType) => {
    // Only the two buyer-app user types are valid here (Lane 1331; the old
    // supplier portal left with Lane 1410 W6). Any other type (for example a
    // sourcing portal token, or an old supplier token) is treated as invalid,
    // so the app logs out without calling any endpoint.
    if (!['admin', 'company'].includes(userType)) return false;
    try {
      const endpoint = `${API_BASE}/api/admin/auth/me`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });
      // Lane 1416: a server error (5xx) says nothing about the token; keep the
      // session instead of signing the browser out. A real refusal is 401/403,
      // and the global interceptor handles 401 on the next call anyway.
      if (response.status >= 500) return true;
      if (!response.ok) return false;

      try {
        const data = await response.json();
        setChatCaptureEnabled(Boolean(data?.chat_capture_enabled));
        setScorecardUiEnabled(Boolean(data?.scorecard_ui_enabled));
        setInventoryUiEnabled(Boolean(data?.inventory_ui_enabled));
        setDocUploadEnabled(Boolean(data?.doc_upload_enabled));
        setNegotiationBriefEnabled(Boolean(data?.negotiation_brief_enabled));
        setOpportunitiesEnabled(Boolean(data?.opportunities_enabled));
        setLeadtimeUiEnabled(Boolean(data?.opportunities_leadtime_enabled));
        setTemplateDecksEnabled(Boolean(data?.template_decks_enabled));
        setDeliveryReliabilityEnabled(Boolean(data?.delivery_reliability_enabled));
        setInventoryOrderingEnabled(Boolean(data?.inventory_ordering_enabled));
        setWorkbookExportEnabled(Boolean(data?.workbook_export_enabled));
        setHasReportArtifacts(Boolean(data?.has_report_artifacts));
        setSourcingEventsEnabled(Boolean(data?.sourcing_events_enabled));
        setSourcingChatIntakeEnabled(data?.sourcing_chat_intake_enabled === true);
        setSourcingIntakeCardV2Enabled(data?.sourcing_intake_card_v2_enabled === true);
        setUploadTablesEnabled(data?.upload_tables_enabled === true);
        setDeckBuilderEnabled(data?.deck_builder_enabled === true);
        setQuotingEnabled(Boolean(data?.quoting_enabled));
        setIntelligencePortalEnabled(Boolean(data?.intelligence_portal_enabled));
        setHomeChatFirstLandingEnabled(Boolean(data?.home_chat_first_landing_enabled));
        setSupplierDecisionsEnabled(Boolean(data?.supplier_decisions_enabled));
        setChatAwaitsDocumentEnabled(Boolean(data?.chat_awaits_document_enabled));
        setChatUploadHonestyEnabled(data?.chat_upload_honesty_enabled === true);
        setDuplicateNonblockingEnabled(
          Boolean(data?.duplicate_notice_nonblocking_enabled));
        // Boolean(undefined) === false, so an older backend that omits the
        // field entirely fails CLOSED to today's consent-gated behaviour.
        setAlwaysOnCaptureEnabled(Boolean(data?.always_on_capture_enabled));
      } catch {
        // Malformed /me body shouldn't invalidate the session — just leave
        // chatCaptureEnabled + scorecardUiEnabled + inventoryUiEnabled at
        // their current values.
      }
      return true;
    } catch {
      // Lane 1416: the network failed, the token was not refused; keep it.
      return true;
    } finally {
      // Lane 1369: the flags are as final as they will get for this session,
      // whether /me answered or not.
      setMeFlagsResolved(true);
    }
  };

  // Login function
  const login = async (email, password, userType = 'company') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, user_type: userType })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store in state
      setToken(data.token);
      setUser(data.user);

      // Persist to localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('authUser', JSON.stringify(data.user));

      // Fetch /me once to populate chat_capture_enabled — the login response
      // deliberately does not carry this field (D.1b). Fire-and-forget: if
      // the call fails, default stays false. We do not await this before
      // returning success; the banner's render gate is idempotent.
      verifyToken(data.token, userType).catch(() => {});

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // SSO login (Lane 1252). The /api/sso/exchange response is byte-shaped
  // like the password login response, so this is the same storage path as
  // login() minus the credential POST — state + localStorage + the same
  // fire-and-forget /me fetch that populates the feature flags.
  const loginWithToken = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(newUser));
    verifyToken(newToken, newUser?.user_type || 'company').catch(() => {});
  };

  // Logout function
  const logout = () => {
    setToken(null);
    setUser(null);
    setChatCaptureEnabled(false);
    setScorecardUiEnabled(false);
    setInventoryUiEnabled(false);
    setOpportunitiesEnabled(false);
    setLeadtimeUiEnabled(false);
    setTemplateDecksEnabled(false);
    setInventoryOrderingEnabled(false);
    setWorkbookExportEnabled(false);
    setHasReportArtifacts(false);
    setSourcingEventsEnabled(false);
    setSourcingChatIntakeEnabled(false);
    setSourcingIntakeCardV2Enabled(false);
    setUploadTablesEnabled(false);
    setDeckBuilderEnabled(false);
    setMeFlagsResolved(false);
    setQuotingEnabled(false);
    setIntelligencePortalEnabled(false);
    setHomeChatFirstLandingEnabled(false);
    setSupplierDecisionsEnabled(false);
    setChatAwaitsDocumentEnabled(false);
    setDeliveryReliabilityEnabled(false);
    setDocUploadEnabled(false);
    setNegotiationBriefEnabled(false);
    // Lane 863. Reset to the closed default like every flag above: without this
    // the flag survives a user switch inside the SPA, so a logout-then-login as
    // a DIFFERENT user would carry the previous session's always-on posture into
    // a page that should be deciding it fresh from that user's own /me.
    setAlwaysOnCaptureEnabled(false);
    // ⚠️ THE PER-USER CONSENT KEYS ARE DELIBERATELY NOT CLEARED HERE, and were
    // not cleared before this lane either. `chat_capture_consent_v1_<user_id>`
    // is per-USER, so it does not leak across a user switch; and clearing it
    // would be an IRREVERSIBLE side effect of a REVERSIBLE flag — flip
    // ALWAYS_ON_CAPTURE_ENABLED back off and every user who had already answered
    // would be re-prompted. A dormant key is the correct dormant state.
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Check if user is authenticated
  const isAuthenticated = !!token && !!user;

  // Check if user is admin
  const isAdmin = user?.user_type === 'admin';

  // Check if user is company user
  const isCompanyUser = user?.user_type === 'company';

  // Get auth header for API calls
  const getAuthHeader = () => {
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
  };

  // Authenticated fetch wrapper
  const authFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers,
      ...getAuthHeader()
    };

    const response = await fetch(url, { ...options, headers });

    // If unauthorized, logout
    if (response.status === 401) {
      logout();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    logout,
    isAuthenticated,
    isAdmin,
    isCompanyUser,
    getAuthHeader,
    authFetch,
    chatCaptureEnabled,
    scorecardUiEnabled,
    inventoryUiEnabled,
    opportunitiesEnabled,
    leadtimeUiEnabled,
    templateDecksEnabled,
    deliveryReliabilityEnabled,
    inventoryOrderingEnabled,
    workbookExportEnabled,
    hasReportArtifacts,
    sourcingEventsEnabled,
    sourcingChatIntakeEnabled,
    sourcingIntakeCardV2Enabled,
    uploadTablesEnabled,
    deckBuilderEnabled,
    meFlagsResolved,
    quotingEnabled,
    intelligencePortalEnabled,
    homeChatFirstLandingEnabled,
    supplierDecisionsEnabled,
    chatAwaitsDocumentEnabled,
    chatUploadHonestyEnabled,
    duplicateNonblockingEnabled,
    docUploadEnabled,
    negotiationBriefEnabled,
    alwaysOnCaptureEnabled,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
